import { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { MenuTestPage } from '../pages/MenuTestPage'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import { useAuth } from '../modules/auth'

// Import all pages
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import ResellersPage from '../pages/reseller/ResellerPage'
import CountriesPage from '../pages/country/CountryPage'
import ProvincesPage from '../pages/province/ProvincePage'
import DistrictsPage from '../pages/district/DistrictPage'
import LanguagesPage from '../pages/language/LanguagePage'
import ServiceCategoryPage from '../pages/service-category/ServiceCategoryPage'
import ServicesPage from '../pages/service/ServicePage'
import BundlesPage from '../pages/bundle/BundlePage'
import AdvertisementsPage from '../pages/advertisement/AdvertisementPage'
import CurrenciesPage from '../pages/currency/CurrencyPage'
import OrdersPage from '../pages/order/OrderPage'
import CompaniesPage from '../pages/company/CompanyPage'
import CompanyCodesPage from '../pages/companycode/CompanyCodePage'
import TransactionsPage from '../pages/transaction/TransactionPage'
import AppSettingPage from '../pages/app-setting/AppSettingPage'
import PaymentMethodsPage from '../pages/paymentmethod/PaymentMethodPage'
import SuppliersPage from '../pages/supplier/SupplierPage'
import PurchasedProductsPage from '../pages/purchasedproduct/PurchasedProductPage'
import PaymentsPage from '../pages/payment/PaymentPage'
import BalancesPage from '../pages/balance/BalancePage'
import ServiceCategorySubTypesPage from '../pages/servicecategorysubtype/ServiceCategorySubTypePage'
import RolesPermissionsPage from '../pages/role-permission/RolePermissionPage'
import UsersPage from '../pages/users/UserPage'
import ResellerGroupsPage from '../pages/reseller-group/ResellerGroupPage'
import GroupDiscountsPage from '../pages/group-discount/GroupDiscountPage'
import GroupPricingsPage from '../pages/group-pricing/GroupPricingPage'
import HawalasPage from '../pages/hawala/HawalaPage'
import HawalaBranchPage from '../pages/hawala-branches/HawalaBranchPage'

// Lazy load modules
const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))

// Permission checking helper function
const checkPermission = (permissions: string[], requiredPermission: string) => {
  return permissions.includes(requiredPermission)
}

const PrivateRoutes = () => {
  const { auth } = useAuth()
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />

        {/* Routes with permission checks */}
        {checkPermission(auth?.permissions, 'manage-roles') && (
          <Route
            path='admin/pages/roles-permissions'
            element={
              <SuspensedView>
                <RolesPermissionsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-resellers') && (
          <Route
            path='admin/pages/resellers/*'
            element={
              <SuspensedView>
                <ResellersPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-orders') && (
          <Route
            path='admin/pages/orders/*'
            element={
              <SuspensedView>
                <OrdersPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-bundles') && (
          <Route
            path='admin/pages/bundles/*'
            element={
              <SuspensedView>
                <BundlesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-countries') && (
          <Route
            path='admin/pages/countries'
            element={
              <SuspensedView>
                <CountriesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-provinces') && (
          <Route
            path='admin/pages/provinces'
            element={
              <SuspensedView>
                <ProvincesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-districts') && (
          <Route
            path='admin/pages/districts'
            element={
              <SuspensedView>
                <DistrictsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-languages') && (
          <Route
            path='admin/pages/languages'
            element={
              <SuspensedView>
                <LanguagesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-service-categories') && (
          <Route
            path='admin/pages/service_category'
            element={
              <SuspensedView>
                <ServiceCategoryPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-service-category-sub-types') && (
          <Route
            path='admin/pages/service_category_sub_type'
            element={
              <SuspensedView>
                <ServiceCategorySubTypesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-services') && (
          <Route
            path='admin/pages/services'
            element={
              <SuspensedView>
                <ServicesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-advertisements') && (
          <Route
            path='admin/pages/advertisements'
            element={
              <SuspensedView>
                <AdvertisementsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-currencies') && (
          <Route
            path='admin/pages/currencies'
            element={
              <SuspensedView>
                <CurrenciesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-transactions') && (
          <Route
            path='admin/pages/transactions/*'
            element={
              <SuspensedView>
                <TransactionsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-companies') && (
          <Route
            path='admin/pages/companies'
            element={
              <SuspensedView>
                <CompaniesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-company-codes') && (
          <Route
            path='admin/pages/company_codes'
            element={
              <SuspensedView>
                <CompanyCodesPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-app-settings') && (
          <Route
            path='admin/pages/app_setting'
            element={
              <SuspensedView>
                <AppSettingPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-payment-methods') && (
          <Route
            path='admin/pages/payment-methods'
            element={
              <SuspensedView>
                <PaymentMethodsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-suppliers') && (
          <Route
            path='admin/pages/suppliers'
            element={
              <SuspensedView>
                <SuppliersPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-users') && (
          <Route
            path='admin/pages/users'
            element={
              <SuspensedView>
                <UsersPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-purchased-products') && (
          <Route
            path='admin/pages/purchased-products'
            element={
              <SuspensedView>
                <PurchasedProductsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-payments') && (
          <Route
            path='admin/pages/payments'
            element={
              <SuspensedView>
                <PaymentsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-balances') && (
          <Route
            path='admin/pages/balances'
            element={
              <SuspensedView>
                <BalancesPage />
              </SuspensedView>
            }
          />
        )}

        {checkPermission(auth?.permissions, 'manage-reseller-groups') && (

          <Route
            path='admin/pages/reseller-groups'
            element={
              <SuspensedView>
                <ResellerGroupsPage />
              </SuspensedView>
            }
          />
        )}
        {checkPermission(auth?.permissions, 'manage-group-discounts') && (

          <Route
            path='admin/pages/group-discounts'
            element={
              <SuspensedView>
                <GroupDiscountsPage />
              </SuspensedView>
            }
          />
        )}
          {checkPermission(auth?.permissions, 'manage-group-discounts') && (

          <Route
            path='admin/pages/group-pricings'
            element={
              <SuspensedView>
                <GroupPricingsPage />
              </SuspensedView>
            }
          />
        )}


      <Route
        path='admin/pages/hawalas'
        element={
          <SuspensedView>
            <HawalasPage />
          </SuspensedView>
        }
      />
            <Route
        path='admin/pages/hawala-branches'
        element={
          <SuspensedView>
            <HawalaBranchPage />
          </SuspensedView>
        }
      />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
